<!--
- 省市区级联组件
- 概要：根据已选区域查询下一级省市区，并默认选中已选区域。每次选择都会将已选区域信息回传给父级组件。
-
- props:
- 1.selected(已选区域，可选)
- 2.index(用于标记级联组件，可选)
-
- emit:
- 1.重新获取选中区域信息
- recapture({selectedOptions: ['', '', ''], index: 0})
-
- @author Qiutm 2018-07-11 17:11:12
-->
<template>
  <el-cascader
    :options="options"
    v-model="selectedOptions"
    change-on-select
    clearable
    :disabled="!isCanChange"
    @change="handleItemChange"/>
</template>

<script>
import { queryAreaInfo } from '@/model/area-model'
export default {
  name: 'ProvinceCityArea',
  props: {
    // 已选区域信息
    selected: {
      type: Array,
      default () {
        return ['', '', '']
      }
    },
    // 用于标记级联组件
    index: {
      type: Number,
      default: 0
    },
    // 用于标记s是否可以修改
    isCanChange: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      // 查询出的选项信息
      options: [],
      // 选中的选项，默认是传进来的已选区域信息  ['', '', '']
      selectedOptions: this.selected,
      provinces: [],
      citys: [],
      regions: []
    }
  },
  watch: {
    // 监听选中的数据，并通知父组件
    selectedOptions (newOption) {
      let option = this.serializeOptions(newOption)
      this.$emit('recapture', {selectedOptions: option, index: this.index})
    },
    selected () {
      this.initData()
      this.selectedOptions = this.selected
    }
  },
  methods: {
    /**
     * 选项适配器
     * @param [array] param [查询条件]
     * @param [array] areas [区域信息]
     */
    optionsAdapter (param, areas) {
      let data = []
      let id
      let name
      if (areas.length > 0) {
        // 1.转换数据格式 --begin
        if (areas[0].provinceId) {
          id = 'provinceId'
          name = 'provinceName'
          this.provinces = areas
        } else if (areas[0].cityId) {
          id = 'cityId'
          name = 'cityName'
          this.citys = areas
        } else if (areas[0].regionId) {
          id = 'regionId'
          name = 'regionName'
          this.regions = areas
        }
        areas.forEach(area => {
          data.push({value: area[id], label: area[name]})
        })// 1.转换数据格式 --end
        // 2.更新选项数据 --begin
        switch (id) {
          case 'provinceId':
            this.updateProvinceOptions(data)
            break
          case 'cityId':
            this.updateCityOptions(data, param.provinceId)
            break
          case 'regionId':
            this.updateRegionOptions(data, param.provinceId, param.cityId)
            break
        }// 2.更新选项数据 --end
      }
    },

    // 更新省选项
    updateProvinceOptions (data) {
      this.options = []
      data.forEach(val => {
        this.options.push({
          value: val.value,
          label: val.label,
          children: []
        })
      })
    },

    /**
     * 更新市选项
     * @param [array] data [市选项数据]
     * @param [string] provinceId  [省id]
     */
    updateCityOptions (data, provinceId) {
      let len = this.options.length
      for (let i = 0; i < len; i++) {
        if (this.options[i].value === provinceId) {
          this.options[i].children = []
          data.forEach(val => {
            this.options[i].children.push({
              value: val.value,
              label: val.label,
              children: []
            })
          })
          break
        }
      }
    },

    /**
     * 更新区选项
     * @param [array] data [区选项数据]
     * @param [string] provinceId  [省id]
     * @param [string] cityId  [区id]
     */
    updateRegionOptions (data, provinceId, cityId) {
      let len = this.options.length
      for (let i = 0; i < len; i++) {
        if (this.options[i].value === provinceId) {
          let option = this.options[i]
          for (let j = 0; j < option.children.length; j++) {
            if (option.children[j].value === cityId) {
              this.options[i].children[j].children = data
              break
            }
          }
          break
        }
      }
    },

    // 查询某一区域信息
    queryAreas (param) {
      return queryAreaInfo(param).then(result => {
        let data = result.data
        if (data && data.length !== 0) {
          this.optionsAdapter(param, data)
        }
      })
    },

    // 级联选项变化
    handleItemChange (option) {
      this.queryAreas({
        provinceId: option[0],
        cityId: option[1],
        regionId: option[2]}
      )
    },

    /**
     * 序列化选项
     * @param [array] options
     * @return [array] processedOptions
     */
    serializeOptions (options) {
      let province = this.provinces.filter(val => val.provinceId === options[0])
      let city = this.citys.filter(val => val.cityId === options[1])
      let region = this.regions.filter(val => val.regionId === options[2])
      let provinceLabel = province[0]
      let cityLabel = city[0]
      let regionLabel = region[0]
      let processedOptions = [{
        value: options[0],
        label: provinceLabel ? provinceLabel.provinceName : ''
      }, {
        value: options[1],
        label: cityLabel ? cityLabel.cityName : ''
      }, {
        value: options[2],
        label: regionLabel ? regionLabel.regionName : ''
      }]
      return processedOptions
    },

    // 清空选项
    clearOptions () {
      this.selectedOptions = []
    },
    initData () {
      // 1.查询区域信息
      let selected = this.selected
      // 是否有待查询条件
      let hasSelected = (selected[0] !== '' || selected[1] !== '' || selected[2] !== '')
      // 查省
      let promise = this.queryAreas()
      if (hasSelected) {
        promise.then(() => {
        // 查市
          return this.queryAreas({
            provinceId: selected[0],
            cityId: '',
            regionId: ''
          })
        }).then(() => {
        // 查区
          return this.queryAreas({
            provinceId: selected[0],
            cityId: selected[1],
            regionId: ''
          })
        }).catch((e) => {
          console.log(e)
        })
      }
    }
  },
  created () {
    this.initData()
  }
}
</script>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/custom/ProvinceCityArea.vue